// Hooks React Query do módulo Chat (PRD 2)

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, useCallback } from "react";
import chatService from "@/services/chatService";
import { getWebSocketUrl } from "@/services/api";
import { lerTokenDeAcesso } from "@/lib/authToken";
import { chaveDaLigacaoNaSala } from "@/lib/chamadas/chaves";
import { comMensagemEditada, manterAnteriores, semAMensagem } from "./mensagensAnteriores";
import type {
  ChatMessage,
  ChatMessageCreate,
  ChatRoom,
  ChatRoomCreate,
  ChatRoomReadState,
  ChatRoomMemberReadState,
  MessageDeliveryStatus,
  WSChatMessage,
  WSChatSendMessage,
} from "@/types/chat";

// Exportados (Story X1.3.3): o flush da fila offline reconcilia este cache.
export const ROOMS_KEY = ["chat", "rooms"];
export const MESSAGES_KEY = (roomId: string) => ["chat", "rooms", roomId, "messages"];
const MEMBERS_KEY = (roomId: string) => ["chat", "rooms", roomId, "members"];
// Story E6.3.3 — snapshot dos cursores delivered/read da sala.
const READ_STATE_KEY = (roomId: string) => ["chat", "rooms", roomId, "read-state"];

/**
 * Casa SÓ a query da LISTA de salas (`["chat","rooms"]` ou
 * `["chat","rooms", <params>]`) e NUNCA uma query escopada numa sala
 * (`["chat","rooms", roomId, ...]`, onde `k[2]` é o roomId string).
 *
 * Usado pra refrescar o sidebar após um self-leave SEM refazer fetch das
 * sub-rotas da sala abandonada — o backend responde 404 pra não-membro
 * (info-leak protection), então invalidar com prefixo `["chat","rooms"]`
 * (que casaria as keys escopadas na sala) dispararia 404 em cascata.
 */
export function isChatRoomsListKey(query: { queryKey: readonly unknown[] }): boolean {
  const k = query.queryKey;
  return (
    k[0] === "chat" &&
    k[1] === "rooms" &&
    (k.length === 2 || (k.length === 3 && (k[2] == null || typeof k[2] === "object")))
  );
}

// ───────────────────────────────────────────────────────────────
// Rooms
// ───────────────────────────────────────────────────────────────

/**
 * Ordena salas pela "última interação" (Story E0.1.2 / FR-E0-4):
 *   1. Salas com mensagens → `last_interaction_at DESC`
 *   2. Salas sem mensagens → `created_at DESC` (no final)
 *
 * O backend já devolve nessa ordem, mas o `select` aqui blinda contra:
 * (a) caches React Query servindo data antiga após WS invalidate,
 * (b) mutations otimísticas que reordenam in-flight.
 */
function sortRoomsByLastInteraction(rooms: ChatRoom[]): ChatRoom[] {
  return [...rooms].sort((a, b) => {
    const aTs = a.last_interaction_at ? new Date(a.last_interaction_at).getTime() : null;
    const bTs = b.last_interaction_at ? new Date(b.last_interaction_at).getTime() : null;
    if (aTs !== null && bTs !== null) return bTs - aTs;
    if (aTs !== null) return -1;
    if (bTs !== null) return 1;
    // Ambos sem msg → tie-break por created_at DESC.
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
}

/**
 * As opções da query da LISTA de salas, num lugar só (D2, passo 14).
 *
 * A tela do chat (`useChatRooms`) e o contador do menu (`useNaoLidasDoChat`) leem a MESMA query:
 * mesma chave, mesma função, mesmo `staleTime`. Se cada um montasse as suas, o menu abriria uma
 * segunda busca de `GET /chat/rooms` e o número do menu poderia discordar da lista.
 */
export function opcoesDaListaDeSalas(params?: { archived?: boolean; room_type?: string }) {
  return {
    queryKey: [...ROOMS_KEY, params],
    queryFn: () => chatService.listRooms(params),
    staleTime: 30_000,
    select: sortRoomsByLastInteraction,
  };
}

export function useChatRooms(params?: { archived?: boolean; room_type?: string }) {
  return useQuery(opcoesDaListaDeSalas(params));
}

export function useChatRoom(roomId: string | null) {
  return useQuery({
    queryKey: ["chat", "rooms", roomId],
    queryFn: () => chatService.getRoom(roomId!),
    enabled: !!roomId,
    staleTime: 60_000,
  });
}

export function useCreateRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ChatRoomCreate) => chatService.createRoom(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ROOMS_KEY }),
  });
}

export function useArchiveRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roomId: string) => chatService.archiveRoom(roomId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ROOMS_KEY }),
  });
}

export function useUpdateRoom(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<ChatRoomCreate>) => chatService.updateRoom(roomId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROOMS_KEY });
      qc.invalidateQueries({ queryKey: ["chat", "rooms", roomId] });
    },
  });
}

export function useExportChatHistory(roomId: string) {
  return useMutation({
    mutationFn: () => chatService.exportHistory(roomId),
  });
}

// ───────────────────────────────────────────────────────────────
// Members
// ───────────────────────────────────────────────────────────────

export function useChatRoomMembers(roomId: string | null) {
  return useQuery({
    queryKey: MEMBERS_KEY(roomId || ""),
    queryFn: () => chatService.listMembers(roomId!),
    enabled: !!roomId,
  });
}

export function useAddMember(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, isAdmin }: { userId: string; isAdmin?: boolean }) =>
      chatService.addMember(roomId, userId, isAdmin),
    onSuccess: () => qc.invalidateQueries({ queryKey: MEMBERS_KEY(roomId) }),
  });
}

/**
 * Remove um membro da sala. O `roomId` vai na VARIÁVEL da mutation (não no
 * fechamento do hook) de propósito: o caller (self-leave) desseleciona a sala
 * ANTES do `await`, então um `roomId` derivado de `selectedRoom?.id` no render
 * viraria `""` e o DELETE iria pra `/chat/rooms//members/...` (404). Capturar
 * `room.id` e passá-lo no `mutateAsync` blinda contra isso.
 *
 * `currentUserId` distingue:
 * - **self-leave** (`userId === currentUserId`): viramos não-membro → toda GET
 *   escopada na sala vira 404 (info-leak protection). Refrescamos APENAS a
 *   lista de salas (a sala some do sidebar); NÃO tocamos nas queries da sala.
 *   O caller desseleciona a sala (observers `enabled:false`) antes do DELETE
 *   resolver, então nenhum refetch escopado na sala dispara.
 * - **kick-other** (admin remove outro): seguimos membro → refetch do member
 *   list pra refletir a remoção.
 */
export function useRemoveMember(currentUserId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roomId, userId }: { roomId: string; userId: string }) =>
      chatService.removeMember(roomId, userId),
    onSuccess: (_data, { roomId, userId }) => {
      if (userId === currentUserId) {
        qc.invalidateQueries({ predicate: isChatRoomsListKey });
      } else {
        qc.invalidateQueries({ queryKey: MEMBERS_KEY(roomId) });
      }
    },
  });
}

// ───────────────────────────────────────────────────────────────
// Messages
// ───────────────────────────────────────────────────────────────

export function useChatMessages(roomId: string | null, params?: { limit?: number }) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: MESSAGES_KEY(roomId || ""),
    queryFn: async () => {
      const frescas = await chatService.listMessages(roomId!, params);
      // As páginas ANTERIORES já carregadas ("Carregar mais", `?nota=`) sobrevivem ao refetch da primeira.
      return manterAnteriores(frescas, qc.getQueryData<ChatMessage[]>(MESSAGES_KEY(roomId!)));
    },
    enabled: !!roomId,
    staleTime: 5_000,
  });
}

/**
 * Acrescenta a mensagem ao cache SEM duplicar.
 *
 * Dois caminhos trazem a mesma mensagem: a resposta REST do envio e o eco do
 * servidor pelo WebSocket. Ate 05/09/2026 isso nao aparecia porque o handler
 * de WS empurrava `undefined` (defeito de contrato, corrigido em `6076d458`);
 * consertada a queda, a duplicacao ficou visivel por 1-2s ate um refetch
 * reconciliar. Aqui ela deixa de existir.
 *
 * Substituo em vez de ignorar quando o id ja existe: a versao mais nova pode
 * trazer campo que a primeira nao tinha (a url reassinada da figurinha, por
 * exemplo, que so o render do servidor injeta).
 */
export function comMensagem(old: ChatMessage[] | undefined, nova: ChatMessage): ChatMessage[] {
  if (!old) return [nova];
  const i = old.findIndex((m) => m?.id === nova.id);
  if (i === -1) return [...old, nova];
  const copia = [...old];
  copia[i] = nova;
  return copia;
}

/**
 * 🔴 A sala vai COM cada envio (`mutateAsync({ roomId, data })`), não presa ao hook.
 *
 * O compositor envia anexos, documentos do Drive e áudio em laço (`await onSend(...)`), e o
 * laço segue rodando depois que a pessoa troca de conversa. No TanStack v5 o observador
 * troca as opções a cada render e cada `mutateAsync` usa as opções ATUAIS: com a sala
 * amarrada no `mutationFn`/`onSuccess`, do 2º envio em diante tudo ia para a sala NOVA (e,
 * no "voltar" do celular, para `/rooms//messages`). Quem chama passa a sala que capturou.
 */
export function useSendMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roomId, data }: { roomId: string; data: ChatMessageCreate }) =>
      chatService.sendMessage(roomId, data),
    // O cache também é da sala de ORIGEM do envio, não da que está aberta agora.
    onSuccess: (newMsg, { roomId }) => {
      qc.setQueryData<ChatMessage[]>(MESSAGES_KEY(roomId), (old) => comMensagem(old, newMsg));
      qc.invalidateQueries({ queryKey: ROOMS_KEY });
    },
  });
}

export function useDeleteMessage(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    // Story E6.2.2/E6.2.3: backend é DELETE /chat/messages/{id} (sem roomId).
    // O WS broadcast (chat.py:_msg_response) inclui room_id no payload, então
    // a invalidação por MESSAGES_KEY(roomId) ainda funciona client-side.
    mutationFn: (messageId: string) => chatService.deleteMessage(messageId),
    onSuccess: (_resposta, messageId) => {
      // Sai do cache NA HORA: o refetch abaixo só refaz a primeira página, e as anteriores já carregadas
      // (`manterAnteriores`) não perderiam a apagada sozinhas.
      qc.setQueryData<ChatMessage[]>(MESSAGES_KEY(roomId), (old) => semAMensagem(old, messageId));
      // A prévia da última mensagem na lista de salas (a linha de Minhas notas) vem do servidor: apagar a
      // última nota a muda, e sem isto o texto apagado ficava na linha até recarregar. Só a LISTA, nunca o
      // prefixo (ver `isChatRoomsListKey`); a prévia não é reescrita à mão (ruling P12).
      void qc.invalidateQueries({ predicate: isChatRoomsListKey });
      return qc.invalidateQueries({ queryKey: MESSAGES_KEY(roomId) });
    },
  });
}

// ───────────────────────────────────────────────────────────────
// Reactions (Story E6.4.3)
// ───────────────────────────────────────────────────────────────

/**
 * Story E6.4.3 — toggle de reaction emoji em uma mensagem.
 *
 * O WS `reaction:update` (mesmo do meu próprio click, via room broadcast) é a fonte de
 * verdade. Aqui só se faz um "early update" no cache, com o que o endpoint devolveu, para
 * quem está sem WS conectado. Não invalidamos MESSAGES_KEY: o cache via WS já refresca.
 *
 * Erro: NÃO avisa aqui. Quem chama (`handleToggleReaction`, na ChatPage) avisa com o motivo do
 * backend; um aviso neste `onError` sairia em dobro. Falhas esperadas: 404 (mensagem apagada
 * por outro, cache velho) e 422 (emoji inválido; a UI já valida o formato, o servidor é a
 * última defesa). Sem rollback de cache: não houve optimistic update.
 */
export function useToggleReaction(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: string; emoji: string }) =>
      chatService.toggleReaction(messageId, emoji),
    onSuccess: (data) => {
      // Fallback pra clientes sem WS conectado (ex: dev tools quebra WS):
      // reduz o cache com o agg que o endpoint retornou.
      qc.setQueryData<ChatMessage[]>(MESSAGES_KEY(roomId), (old) =>
        old?.map((m) => (m.id === data.message_id ? { ...m, reactions: data.reactions } : m))
      );
    },
  });
}

// ───────────────────────────────────────────────────────────────
// 2-check delivery + opt-out (Story E6.3.3)
// ───────────────────────────────────────────────────────────────

/**
 * Story E6.3.3 — snapshot dos cursores delivered/read + flag opt-out de
 * todos os members da sala. Refetch automático a cada 60s (fallback);
 * WS frames `delivery_update` / `read_receipt_update` mantêm o cache
 * fresco em tempo real via `useChatWebSocket`.
 *
 * Inclui o próprio caller — UI filtra trivialmente via current_user.id
 * quando precisa.
 */
export function useChatReadState(roomId: string | null) {
  return useQuery({
    queryKey: READ_STATE_KEY(roomId || ""),
    queryFn: () => chatService.getReadState(roomId!),
    enabled: !!roomId,
    staleTime: 5_000,
  });
}

/**
 * Story E6.3.3 — toggle opt-out de read receipts NESTA sala.
 *
 * Quando `enabled=false`, mark_read futuros do user atual não disparam
 * broadcast → outros sender ven só 1 check pra ele. Reflete imediato
 * via invalidação do read-state cache.
 */
export function useToggleReadReceipts(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => chatService.toggleReadReceipts(roomId, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: READ_STATE_KEY(roomId) }),
  });
}

/**
 * Story E6.3.3 — computa o status visual de uma mensagem do POV do
 * sender (1 check / 2 cinza / 2 azul).
 *
 * Regra:
 * - `read` quando todos os "peers" não-opt-out têm
 *   `last_read_at >= msg.created_at`. Em DM → 1 peer; em GROUP →
 *   todos os outros members.
 * - `delivered` quando todos os peers (independente de opt-out) têm
 *   `last_delivered_at >= msg.created_at`.
 * - `sent` caso contrário.
 *
 * Quando não há peers (sala só com sender — caso degenerado), o
 * status fica `sent` (não há ninguém pra confirmar).
 *
 * Opt-out: peers com `read_receipts_enabled=false` NÃO contam pra
 * decidir `read` (sender vê só 1 check pra ele); MAS contam pra
 * `delivered` — delivery é privacidade técnica, não opt-outável.
 */
export function computeMessageStatus(
  msgCreatedAt: string,
  myUserId: string | null,
  members: ChatRoomMemberReadState[]
): MessageDeliveryStatus {
  const msgTs = new Date(msgCreatedAt).getTime();
  if (!Number.isFinite(msgTs)) return "sent";

  const peers = members.filter((m) => m.user_id !== myUserId);
  if (peers.length === 0) return "sent";

  const hasReadAt = (iso: string | null): boolean => {
    if (!iso) return false;
    const t = new Date(iso).getTime();
    return Number.isFinite(t) && t >= msgTs;
  };

  const allDelivered = peers.every((p) => hasReadAt(p.last_delivered_at));
  if (!allDelivered) return "sent";

  // "read" precisa que TODOS os peers não-opt-out tenham lido. Peers
  // opt-out não contam — se houver pelo menos 1 peer não-opt-out, ele
  // precisa ter lido. Se TODOS os peers são opt-out, fica em `delivered`
  // (não conseguimos provar "lido", então sender vê 2 cinza).
  const eligibleForRead = peers.filter((p) => p.read_receipts_enabled);
  if (eligibleForRead.length === 0) return "delivered";

  const allRead = eligibleForRead.every((p) => hasReadAt(p.last_read_at));
  return allRead ? "read" : "delivered";
}

// ───────────────────────────────────────────────────────────────
// WebSocket — /api/v1/ws/chat/{room_id}
// ───────────────────────────────────────────────────────────────

export interface UseChatWebSocketOptions {
  onMessage?: (msg: WSChatMessage) => void;
  /**
   * Alguém chamou a atenção desta sala.
   *
   * ⚠️ Fica como callback e não como estado: sacudir a tela é um EVENTO, não
   * uma condição. Guardá-lo em estado obrigaria a limpá-lo depois, e dois
   * chamados seguidos com o mesmo valor não disparariam o segundo.
   */
  onNudge?: (de: { userId: string; userName: string }) => void;
  /** O envio foi barrado pelo limite do servidor. `retryIn` em segundos. */
  onNudgeThrottled?: (retryIn: number) => void;
}

export function useChatWebSocket(roomId: string | null, options?: UseChatWebSocketOptions) {
  const wsRef = useRef<WebSocket | null>(null);
  const [connected, setConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState<Map<string, string>>(new Map());
  const qc = useQueryClient();
  const onMessageRef = useRef(options?.onMessage);
  onMessageRef.current = options?.onMessage;
  // 🔑 Em `ref`, como o `onMessage` acima: o efeito do WebSocket depende só de
  //    `roomId`, e pôr as callbacks nas dependências reconectaria o socket a
  //    cada render do componente que as define inline.
  const onNudgeRef = useRef(options?.onNudge);
  onNudgeRef.current = options?.onNudge;
  const onNudgeThrottledRef = useRef(options?.onNudgeThrottled);
  onNudgeThrottledRef.current = options?.onNudgeThrottled;

  useEffect(() => {
    if (!roomId) return;

    // 🔴 Lia `localStorage["access_token"]`, chave LEGADA que o login não
    //    escreve — e o `if (!token) return` logo abaixo fazia o chat NUNCA
    //    conectar para quem não tivesse a sobra de uma sessão antiga. Sem erro,
    //    sem aviso: a sala simplesmente não recebia mensagem em tempo real.
    //    Ver `@/lib/authToken`.
    const token = lerTokenDeAcesso();
    if (!token) return;

    // A URL vem do mesmo lugar que a dos outros WebSockets. A montagem à mão
    // que estava aqui dependia de `apiConfig.baseURL` ser absoluta — em
    // produção ela é `/api/v1`, relativa, e as duas substituições não casavam
    // nada. Funcionava por acidente do navegador resolver a relativa.
    const wsUrl = `${getWebSocketUrl(`/api/v1/ws/chat/${roomId}`)}?token=${token}`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onerror = () => setConnected(false);

    ws.onmessage = (ev) => {
      try {
        const msg: WSChatMessage = JSON.parse(ev.data);
        onMessageRef.current?.(msg);

        if (msg.type === "message:new") {
          // 🔴 O backend manda em `message` (chat.py:620/:1378); isto lia
          // `payload`. O `undefined` resultante era EMPURRADO para o array e
          // o `MessageList` caia em `undefined.created_at`, derrubando a
          // pagina toda. Aceito as duas chaves e RECUSO o evento se nenhuma
          // trouxer objeto — melhor perder um evento que contaminar o cache.
          const nova = msg.message ?? msg.payload;
          if (!nova?.id) return;
          // `comMensagem` e o que impede a figurinha de aparecer DUAS vezes:
          // o envio ja a inseriu pela resposta REST, e isto aqui e o eco.
          qc.setQueryData<ChatMessage[]>(MESSAGES_KEY(roomId), (old) => comMensagem(old, nova));
          // Story E0.1.3: nova msg na sala ativa muda `last_interaction_at`
          // e (potencialmente) `unread_count` se a leitura ainda não foi
          // marcada. Invalidate é barato (1 sala visível ≠ refetch global).
          qc.invalidateQueries({ queryKey: ROOMS_KEY });
        } else if (msg.type === "message:edit" || msg.type === "message:edited") {
          // O backend emite `message:edited` (chat.py:704) — este ramo so
          // reconhecia `message:edit`, entao a edicao NUNCA chegava na tela
          // por WebSocket. As duas formas passam a ser aceitas.
          const editada = msg.message ?? msg.payload;
          if (!editada?.id) return;
          // MESCLA por cima da mensagem do cache (a mesma do `useEditMessage`), e não troca: o eco traz
          // SEMPRE `reactions: {}` e `reply_count: 0` (defaults do `_msg_response`), e `comMensagemEditada`
          // não os copia — editar não muda reação nem resposta. Trocar (ou mesclar tudo) apagava as reações
          // da mensagem na tela e, numa de PÁGINA ANTERIOR, nem a recarga as devolvia.
          qc.setQueryData<ChatMessage[]>(MESSAGES_KEY(roomId), (old) =>
            comMensagemEditada(old, editada)
          );
        } else if (msg.type === "message:deleted") {
          // 🔴 O backend manda `message:deleted` com `message_id` (chat.py,
          //    `delete_message`); o front só escutava `message:delete` — a mensagem
          //    apagada ficava na tela dos outros até recarregar. Recarrega como o
          //    `useDeleteMessage` faz para quem apagou: tira da lista na hora (as páginas
          //    anteriores já carregadas não a perderiam sozinhas) e reconfirma pelo servidor.
          //    A apagada NÃO volta, nem como tombstone: `list_messages` filtra
          //    `deleted_at IS NULL` por padrão (chat_service.py) — os dois lados veem o mesmo.
          qc.setQueryData<ChatMessage[]>(MESSAGES_KEY(roomId), (old) =>
            semAMensagem(old, msg.message_id)
          );
          qc.invalidateQueries({ queryKey: MESSAGES_KEY(roomId) });
        } else if (msg.type === "message:delete") {
          // Formato antigo (mensagem inteira no evento): segue tirando da lista.
          const alvo = msg.message ?? msg.payload;
          if (!alvo?.id) return;
          qc.setQueryData<ChatMessage[]>(MESSAGES_KEY(roomId), (old) => semAMensagem(old, alvo.id));
        } else if (msg.type === "user_typing") {
          setTypingUsers((m) => new Map(m).set(msg.user_id, msg.user_name));
        } else if (msg.type === "user_stop_typing") {
          setTypingUsers((m) => {
            const next = new Map(m);
            next.delete(msg.user_id);
            return next;
          });
        } else if (msg.type === "nudge") {
          onNudgeRef.current?.({ userId: msg.user_id, userName: msg.user_name });
        } else if (msg.type === "nudge_throttled") {
          onNudgeThrottledRef.current?.(msg.retry_in);
        } else if (msg.type === "delivery_update") {
          // Story E6.3.3 — backend confirmou delivered pra esses user_ids.
          // Atualiza o cursor last_delivered_at em cache.
          const tsIso = msg.last_delivered_at;
          qc.setQueryData<ChatRoomReadState>(READ_STATE_KEY(roomId), (old) => {
            if (!old) return old;
            const userIdSet = new Set(msg.user_ids);
            return {
              ...old,
              members: old.members.map((m) =>
                userIdSet.has(m.user_id) ? { ...m, last_delivered_at: tsIso } : m
              ),
            };
          });
        } else if (msg.type === "read_receipt_update") {
          // Story E6.3.3 — um peer (ou o próprio user) avançou cursor de
          // leitura. Atualiza cache; sender renderiza 2 checks azuis.
          const { user_id, last_read_at } = msg;
          qc.setQueryData<ChatRoomReadState>(READ_STATE_KEY(roomId), (old) => {
            if (!old) return old;
            return {
              ...old,
              members: old.members.map((m) => (m.user_id === user_id ? { ...m, last_read_at } : m)),
            };
          });
        } else if (msg.type === "reaction:update") {
          // Story E6.4.3 — broadcast pós-toggle. Reduz o cache trocando
          // `reactions` da msg-alvo pelo agg novo. Funciona pra qualquer
          // peer reagindo (inclusive eu, eco do meu próprio click).
          const { message_id, reactions } = msg;
          qc.setQueryData<ChatMessage[]>(MESSAGES_KEY(roomId), (old) =>
            old?.map((m) => (m.id === message_id ? { ...m, reactions } : m))
          );
        } else if (msg.type === "reply_count:update") {
          // Story E6.4.3 — reply nova/deletada bumpa o contador do parent.
          // Bump direto no cache mantém o badge "N respostas" fresh.
          const { parent_message_id, reply_count } = msg;
          qc.setQueryData<ChatMessage[]>(MESSAGES_KEY(roomId), (old) =>
            old?.map((m) => (m.id === parent_message_id ? { ...m, reply_count } : m))
          );
        } else if (msg.type === "chamada:sala") {
          // Ligação de GRUPO começou ou acabou: a faixa "Entrar" refaz a leitura da ligação ativa DESTA sala. Só
          // invalida (a faixa é quem lê); as mensagens não mudam, o registro `call_event` chega pelo `message:new`.
          qc.invalidateQueries({ queryKey: chaveDaLigacaoNaSala(roomId) });
        } else if (msg.type === "group:updated") {
          // A regra ou a info do grupo mudou (o dono trocou "todos" por "donos", renomeou, trocou a foto): a tela
          // relê a LISTA de salas, de onde sai o `pode_no_grupo` vivo que a ChatPage dá ao painel e ao compositor.
          // 🔴 Só a lista (`isChatRoomsListKey`): o prefixo `["chat","rooms"]` casaria as queries escopadas na
          //    sala, e o servidor responde 404 a quem acabou de sair do grupo (404 em cascata).
          qc.invalidateQueries({ predicate: isChatRoomsListKey });
        } else if (msg.type === "member:role_changed") {
          // Promoção ou rebaixamento muda o `pode_no_grupo` de quem mudou de papel (lista de salas) e a marca de
          // moderador na lista de membros da sala. `member:added` e `member:removed` ficam de fora: a regra não mudou.
          qc.invalidateQueries({ predicate: isChatRoomsListKey });
          qc.invalidateQueries({ queryKey: MEMBERS_KEY(roomId) });
        }
      } catch {
        // Ignora payload malformado
      }
    };

    // Ping a cada 30s pra manter alive
    const pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "ping" } as WSChatSendMessage));
      }
    }, 30_000);

    return () => {
      clearInterval(pingInterval);
      ws.close();
      wsRef.current = null;
    };
  }, [roomId, qc]);

  const sendTyping = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "typing" } as WSChatSendMessage));
    }
  }, []);

  const sendStopTyping = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "stop_typing" } as WSChatSendMessage));
    }
  }, []);

  const markRead = useCallback((ts?: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "mark_read", ts } as WSChatSendMessage));
    }
  }, []);

  /**
   * Chama a atenção de quem está na sala.
   *
   * ⚠️ Não há limite aqui de propósito. O limite mora no servidor
   * (`CHAT_NUDGE_THROTTLE_SECONDS`), porque um botão desabilitado na tela
   * é sugestão: quem quiser insistir recarrega a página. Aqui só se
   * verifica se o socket está aberto.
   */
  const sendNudge = useCallback(() => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "nudge" }));
    }
  }, []);

  return { connected, typingUsers, sendTyping, sendStopTyping, markRead, sendNudge };
}

/**
 * Story E6.4.2 — busca tenant-wide membership-gated por hashtag.
 *
 * Habilitado só quando `tag` é truthy e `enabled !== false` (cobre o caso
 * de modal fechado: hook montado, query em standby). React Query refaz
 * fetch ao abrir o modal pra evitar exibir dados stale entre sessões.
 *
 * Cache key inclui `tag` lowercase pra evitar duplicar entradas com
 * variações de caixa que apontam pra mesma resposta server-side.
 */
export function useHashtagMessages(
  tag: string | null,
  options?: { enabled?: boolean; limit?: number }
) {
  const normalizedTag = tag ? tag.replace(/^#/, "").toLowerCase() : null;
  return useQuery({
    queryKey: ["chat", "hashtag", normalizedTag, options?.limit ?? 50],
    queryFn: () =>
      chatService.listMessagesByHashtag(normalizedTag!, {
        limit: options?.limit ?? 50,
      }),
    enabled: !!normalizedTag && options?.enabled !== false,
    staleTime: 10_000,
  });
}
